// #tag: EXAMINATION RESULTS SCREEN
// #purpose: Displays searchable, category-filtered examination scorecards, merit lists, cutoff announcements, and direct result links.
import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { FilterChips, ResultCard } from '../components/common/Cards';
import { PageHeaderSection } from '../components/cms/PageSections';
import { EmptyState, ErrorState, LoadingState } from '../components/states/StateViews';
import { useSearchSounds } from '../hooks/useSearchSounds';
import { useExamTypes, usePageSections, useResultCategories, useResults } from '../hooks/useExamData';
import type { ExamCategory, ResultCategory } from '../types';

export function ResultsScreen() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<ResultCategory | 'all'>('all');
  const [examFilter, setExamFilter] = useState<string>('all');

  const { data: sectionsData } = usePageSections('results');
  const headerSection = sectionsData?.data?.[0];
  const { data: categoriesData } = useResultCategories();
  const { data: examTypesData } = useExamTypes();
  const examTypeNames = (examTypesData?.data ?? []).map((t) => t.name);

  const { data, isLoading, isError, refetch } = useResults({
    q: query || undefined,
    category: category === 'all' ? undefined : category,
    examCategory: examFilter === 'all' ? undefined : examFilter,
  });

  const results = data?.data ?? [];
  const total = data?.pagination.total ?? results.length;
  const { onSearchKeyDown } = useSearchSounds(query, total);

  if (isLoading && !data) {
    return (
      <section className="adaptive-page">
        <LoadingState label="Loading results…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load results. Make sure the API server is running." onRetry={() => refetch()} playSound={false} />
      </section>
    );
  }

  return (
    <section className="adaptive-page">
      {headerSection && <PageHeaderSection section={headerSection} />}

      <div className="wfx-search-bar glass-surface">
        <Search size={18} aria-hidden="true" />
        <input
          type="search"
          placeholder="Search results, organizations…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          aria-label="Search results"
        />
      </div>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">Category</span>
        <div className="wfx-filter-chips wfx-scroll-chips" role="group" aria-label="Category filters">
          <button
            type="button"
            className={`chip-pill ${category === 'all' ? 'is-active' : ''}`}
            onClick={() => setCategory('all')}
          >
            All Results
          </button>
          {(categoriesData?.data ?? []).map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`chip-pill ${category === cat.id ? 'is-active' : ''}`}
              onClick={() => setCategory(cat.id as ResultCategory)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">Exam type</span>
        <FilterChips
          options={examTypeNames as ExamCategory[]}
          selected={examFilter as ExamCategory | 'all'}
          onChange={(v) => setExamFilter(v)}
        />
      </div>

      <div className="wfx-results-meta-bar">
        <p className="wfx-result-count">
          {total} result{total !== 1 ? 's' : ''} found
        </p>
      </div>

      {results.length === 0 ? (
        <EmptyState
          title={query ? 'No results for your search' : 'No results match your filters'}
          message={query ? 'Try different keywords or clear your search.' : 'Try changing the category or search term.'}
          illustrationContext={query ? 'empty.search' : 'empty.results'}
        />
      ) : (
        <div className="wfx-result-grid">
          {results.map((result) => (
            <ResultCard key={result.id} result={result} />
          ))}
        </div>
      )}
    </section>
  );
}
